/*
 * Model for strength gauge, including the functionality to send requests to the server
 * to calculate strength
 */

define([
  "backbone",
  "jquery",
  "underscore",
  "messaging/messenger",
  "configuration/properties",
], function (Backbone, $, _, Messenger, Properties) {
  "use strict";

  let StrengthGaugeModel = {};

  //model for a collection of examples that match an ordered list of keywords
  StrengthGaugeModel.matchingExamples = Backbone.Collection.extend({
    getMatches: function (wordList, options) {
      //fires a sync event when finished
      options = _.extend(
        {
          data: JSON.stringify(wordList),
          contentType: "application/json",
          type: "PUT",
        },
        options,
      );
      this.fetch(options);
    },
    url: function () {
      const kbId = Messenger.reqres.request("active:kb");
      return Properties.rootURL + "/" + kbId + "/intents/examples/similar";
    },
  });

  //model for a list of unknown words obtained from an example string
  StrengthGaugeModel.unknownWords = Backbone.Model.extend({
    getUnknownWords: function (exampleString, options) {
      //fires a sync event when finished
      this.url = function () {
        const kbId = Messenger.reqres.request("active:kb");
        return (
          `${Properties.rootURL}/${kbId}/` +
          `lexicon/unknown_words/search` +
          `?text=${encodeURIComponent(exampleString)}`
        );
      };
      this.fetch(options);
    },
    //parse list of strings into an attribute called 'words'
    parse: function (response) {
      const words = [];
      //strip out any numbers because we don't want the user to consider them as unknown
      for (let i = 0; i < response.length; i++) {
        if (!response[i].match(/\b\d+(\.\d+)?/)) {
          words.push(response[i]);
        }
      }
      return {
        words: words,
      };
    },
  });

  StrengthGaugeModel.strengthGaugeModel = Backbone.Model.extend({
    defaults: {
      strength: 0,
    },
    addPenalty: function (penalty) {
      //penalize the current strength while making sure the strength does not go below zero
      const currentStrength = this.get("strength");
      const newStrength = Math.max(0, currentStrength - penalty);
      this.set("strength", newStrength);
    },
    calculateStrength: function (exampleString, topicModel, exampleModel, options) {
      const _this = this;
      //reset strength and errors array
      this.set("strength", 100);
      this.set("errors", []);
      // get selected locale. The strength gauge will be more restricted for locales other than English
      const selectedLocale = Messenger.reqres.request("topic:example:modal:locale");
      const isEnglish = Messenger.reqres.request("kb:language:is:english", selectedLocale);
      //To calculate the strength, we start with a perfect 100 strength and deduct points for each issue we find
      //First check for fatal errors which will reduce the strength to 0
      this._calculateFatalErrors(exampleString, isEnglish);
      //if we didn't find a fatal error, continue looking for other errors
      if (this.get("strength") > 0) {
        //split up the example into words
        const exampleWordArray = exampleString.toLowerCase().split(" ");
        this._calculateLengthPenalty(exampleWordArray);
        let exampleNoStopWordArray;
        if (isEnglish) {
          exampleNoStopWordArray = this._filterOutStopWords(exampleWordArray);
        } else {
          exampleNoStopWordArray = exampleWordArray;
        }

        //this._calculateStopWordPenalty(exampleNoStopWordArray, exampleWordArray);

        //compare the no-stopword array against any examples in this topic
        const examples = topicModel.examples();
        this._calculateSimilarityPenalty(
          exampleNoStopWordArray,
          examples,
          exampleModel,
          topicModel.get("intentName"),
          true,
        );
        //fetch any possible overlapping examples from the server,
        // as well as look for new words (only need to look if exampleNoStopWordArray.length > 0 b/c new words wouldn't be stop words!)
        if (exampleNoStopWordArray.length > 0) {
          //get matching examples
          const exampleMatches = new StrengthGaugeModel.matchingExamples();
          let callCount = 2;
          exampleMatches.getMatches(exampleNoStopWordArray, {
            error: function (model, resp) {
              Messenger.showErrorFromResponse(resp);
            },
            success: function () {
              //remove any examples from this topic (since we already processed them and it could be a match to the example we are currently authoring)
              exampleMatches.each(function (match) {
                if (match.get("intentId") === topicModel.get("intentId")) {
                  match.ignore = true; //flag match to ignore it
                }
              }, this);
              console.log("# of matches: " + exampleMatches.length);
              _this._calculateSimilarityPenalty(
                exampleNoStopWordArray,
                exampleMatches,
                exampleModel,
                topicModel.get("intentName"),
                false,
              );
              --callCount;
              if (callCount === 0) {
                //finally, when we are done, update the strength
                Messenger.vent.trigger("strength:updated", _this.get("strength"), options);
              }
            },
          });
          //get any new words
          const unknownWords = new StrengthGaugeModel.unknownWords();
          unknownWords.getUnknownWords(exampleString, {
            error: function (model, resp) {
              Messenger.showErrorFromResponse(resp);
            },
            success: function () {
              --callCount;
              if (selectedLocale == 1) {
                _this._calculateUnknownWordsPenalty(unknownWords);
              }
              if (callCount === 0) {
                //finally, when we are done, update the strength
                Messenger.vent.trigger("strength:updated", _this.get("strength"), options);
              }
            },
          });
          return;
        }
      }
      Messenger.vent.trigger("strength:updated", this.get("strength"), options);
    },
    initializeCustom: function () {
      // assign default errors array in the initialize function instead of defaults, because adding entries to the
      // array would also add them to the default
      this.set("errors", []);
    },
    _calculateFatalErrors: function (exampleString, isEnglish) {
      // reject an example with a score of zero if:
      // 1) The example contains punctuation (,!?:;) and is English.  Punctuation is more important in non-English
      //    languages and cannot be removed or it changes the meaning.
      // 1.5) Sneaky period check to avoid penalizing acronyms
      // 2) The example contains other symbols (<>"{}[]()\^*=|)
      // 3) The example is of zero length (excluding spaces)
      // 4) The example contains a new line symbol (this should be blocked already by the editor itself, but is here
      //    as a secondary safeguard)
      const errors = this.get("errors");
      if (exampleString.match(/[,!?:;]/) && isEnglish) {
        errors.push($.t("topic.example.errors.punctuationError"));
        this.set("strength", 0);
      }
      //the test for periods is tricky because we don't want to penalize valid acronyms ("U.S.A."), numbers, or email addresses
      //surround the example with a space so that we don't have to deal with start/end of string markers
      const exampleWithExtraSpaces = " " + exampleString + " ";
      //count up the numbers with a decimal point ("3.14159")
      const numberList = exampleWithExtraSpaces.match(/\s\d+\.\d+\s/g);
      const numberCount = numberList ? numberList.length : 0;
      //count the number of acronyms and the number of periods in the acronyms
      const acronymList = exampleWithExtraSpaces.match(/\s(\w\.)*\w\.\w\.\s/g);
      const acronymPeriodCount = this._numberOfPeriods(acronymList);
      //count the number of email addresses and the number of periods in the addresses (regex from http://www.regular-expressions.info/email.html  covers vast majority of valid emails)
      const emailList = exampleWithExtraSpaces.match(
        /\s[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}\s/gi,
      );
      const emailPeriodCount = this._numberOfPeriods(emailList);
      //count the number of periods
      const periodList = exampleString.match(/\./g);
      const periodCount = periodList ? periodList.length : 0;

      //if periods are only being used in acronyms, numbers, and emails, then periodCount - numberCount - acronymPeriodCount - emailPeriodCount equals zero
      if (periodCount - numberCount - acronymPeriodCount - emailPeriodCount > 0 && isEnglish) {
        //unacceptable periods are here!
        errors.push($.t("topic.example.errors.punctuationError"));
        this.set("strength", 0);
      }
      if (exampleString.match(/[<>"{}[\]()\\^*=|]/)) {
        errors.push($.t("topic.example.errors.illegalSymbolError"));
        this.set("strength", 0);
      }
      if (exampleString.length === 0) {
        errors.push($.t("topic.example.errors.zeroLengthError"));
        this.set("strength", 0);
      }
      if (exampleString.match(/[\r\n]/)) {
        errors.push($.t("topic.example.errors.newLineError"));
        this.set("strength", 0);
      }
    },
    _calculateLengthPenalty: function (exampleWordArray) {
      const errors = this.get("errors");
      let penalty;
      if (exampleWordArray.length <= 3) {
        penalty = 10 * (3 - exampleWordArray.length) + 5;
        errors.push($.t("topic.example.errors.shortError"));
        this.addPenalty(penalty);
      } else if (exampleWordArray.length > 12) {
        penalty = Math.min(100, (exampleWordArray.length - 12) * 5);
        errors.push($.t("topic.example.errors.longError"));
        this.addPenalty(penalty);
      }
    },
    //calculates the penalty for similar matches given a list of either exampleMatch objects or TopicExample objects
    //(represented by isTopicExampleList boolean)
    //Passed the example that is currently being edited (exampleModel) so we can check to make sure that we aren't
    //comparing an example to itself
    _calculateSimilarityPenalty: function (
      exampleNoStopWordArray,
      examples,
      exampleModel,
      topicName,
      isTopicExampleList,
    ) {
      const errors = this.get("errors");
      examples.each(function (example) {
        if (!example.ignore) {
          //do not process examples that we are supposed to ignore
          let exampleString;
          if (isTopicExampleList) {
            exampleString = example.getExampleText();
          } else {
            exampleString = example.get("exampleText");
          }
          const exampleArray = exampleString.toLowerCase().split(" ");
          const noStopWordArray = this._filterOutStopWords(exampleArray);
          //if this is a topic example, then we don't know if it is similar to the current example at all, so we need to check
          let isMatch = false;
          if (isTopicExampleList) {
            if (example !== exampleModel) {
              //loop through noStopWordArray and try to match each element of exampleNoStopWordArray in order (allowing for extra elements in between)
              let matches = 0;
              for (let i = 0; i < noStopWordArray.length; i++) {
                if (noStopWordArray[i] === exampleNoStopWordArray[matches]) {
                  matches++;
                  if (matches === exampleNoStopWordArray.length) break;
                }
              }
              if (matches === exampleNoStopWordArray.length) {
                isMatch = true; //matched every word in the example array, in order
              }
            }
          }
          if (isMatch || !isTopicExampleList) {
            //if we matched a TopicExample or this is an exampleMatch (already matched server-side)
            //compare lengths of array to determine if it is an error or penalty
            //if exampleNoStopWordArray length equals noStopWordArray, then this is an "exact" match and is a fatal error
            //for each additional word in noStopWordArray add penalty of 35 - 25*(# of additional words - 1)
            const intentName = isTopicExampleList ? topicName : example.get("intentName");
            if (exampleNoStopWordArray.length === noStopWordArray.length) {
              errors.push(
                $.t("topic.example.errors.fatalSimilarityError", {
                  example: exampleString,
                  intent: intentName,
                }),
              );
              this.addPenalty(100);
            } else if (noStopWordArray.length - exampleNoStopWordArray.length === 1) {
              errors.push(
                $.t("topic.example.errors.nonfatalSimilarityError", {
                  example: exampleString,
                  intent: intentName,
                }),
              );
              const penalty = 15;
              this.addPenalty(penalty);
            }
            //otherwise, no penalty
          }
        }
      }, this);
    },
    _calculateUnknownWordsPenalty: function (unknownWords) {
      const unknownWordsArray = unknownWords.get("words");
      if (unknownWordsArray.length > 0) {
        const errors = this.get("errors");
        //parse out words into a comma separated string
        let wordListString = "";
        for (let i = 0; i < unknownWordsArray.length; i++) {
          wordListString += unknownWordsArray[i];
          if (i < unknownWordsArray.length - 1) {
            wordListString += ", ";
          }
        }
        errors.push(
          $.t("topic.example.errors.unknownWordsError", {
            words: wordListString,
            count: unknownWordsArray.length,
          }),
        );
        //do not add a penalty for unknown words
        this.trigger("change"); //since we don't change the penalty, we need to manually cause a change event for the error to show up
      }
    },
    _filterOutStopWords: function (exampleWordArray) {
      const stopWords = Messenger.reqres.request("kb:stop:words");
      const exampleWithoutStopWordsArray = [];
      const numberOfWords = exampleWordArray.length;
      for (let i = 0; i < numberOfWords; i++) {
        if (_.indexOf(stopWords, exampleWordArray[i]) < 0) {
          //word at this index is not a stop word
          exampleWithoutStopWordsArray.push(exampleWordArray[i]);
        }
      }
      return exampleWithoutStopWordsArray;
    },
    //simply returns the number of periods in a list of strings
    _numberOfPeriods: function (stringList) {
      let count = 0;
      if (stringList) {
        stringList.forEach(function (string) {
          count += string.match(/\./g).length;
        });
      }
      return count;
    },
  });

  //Set the failure threshold
  Messenger.reqres.setHandler("example:failure:threshold", function () {
    return 25;
  });

  return StrengthGaugeModel;
});
